import { AdvertiserPage, AdvertiserPageGroup } from 'services/AdvertiserPagesService';

// What "Page or brand to follow" offers. Following is a flag on a group (the
// daily job, the table and Unfollow all work on groups), so a single page is
// followed by giving it a one-page group of its own, named after the page.

export const FOLLOW_OPTION = Object.freeze({
  GROUP: 'group',
  PAGE: 'page',
} as const);

export type FollowOption =
  | { kind: typeof FOLLOW_OPTION.GROUP; group: AdvertiserPageGroup }
  | { kind: typeof FOLLOW_OPTION.PAGE; page: AdvertiserPage };

/**
 * Every unfollowed group, then every active page not already covered:
 *  - a page in a followed group is already fetched each morning, so following
 *    it again would only add a duplicate row;
 *  - a page with a one-page group of its own (say, one followed here and later
 *    unfollowed) is offered as that group, so it does not appear twice.
 *
 * Expects active groups and pages only, as the Followed brands panel loads them.
 */
export function followOptions(groups: AdvertiserPageGroup[], pages: AdvertiserPage[]): FollowOption[] {
  const covered = new Set<string>();
  for (const g of groups) {
    if (g.followStatics || g.pageIds.length === 1) g.pageIds.forEach(id => covered.add(id));
  }
  return [
    ...groups
      .filter(g => !g.followStatics)
      .map(group => ({ kind: FOLLOW_OPTION.GROUP, group }) as FollowOption),
    ...pages
      .filter(p => p.isActive && !covered.has(p.pageId))
      .map(page => ({ kind: FOLLOW_OPTION.PAGE, page }) as FollowOption),
  ];
}

export function followOptionKey(option: FollowOption): string {
  return option.kind === FOLLOW_OPTION.GROUP ? `group:${option.group.groupId}` : `page:${option.page.pageId}`;
}

export function followOptionLabel(option: FollowOption): string {
  if (option.kind === FOLLOW_OPTION.PAGE) return option.page.pageName;
  const n = option.group.pageIds.length;
  return `${option.group.groupName} (${n} page${n === 1 ? '' : 's'})`;
}
